import React, { useEffect, useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import { useHistory, useLocation } from "react-router-dom";
import parsePhoneNumber from "libphonenumber-js";
import {
  Paper,
  Box,
  Typography,
  Slider,
  CircularProgress,
  withStyles
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  FormActionDrawer,
  IntroText,
  Sheet,
  SheetContent
} from "../../../components";
import { useMutation } from "@apollo/react-hooks";
import { useSnackbar } from "notistack";
import { useConsumerAuth } from "../../../hooks";
import { POST_VAX_YES_PAY } from "../../../queries";
import { ROUTE_PATHS } from "../../../constants";
import { useTranslation } from "react-i18next";

const CustomSlider = withStyles({
  root: {
    height: 6
  },
  thumb: {
    height: 18,
    width: 18,
    marginTop: -6,
    marginLeft: -12,
    "&:focus, &:hover, &$active": {
      boxShadow: "inherit"
    }
  },
  active: {},
  valueLabel: {
    left: "calc(-50% + 4px)"
  },
  track: {
    height: 6,
    borderRadius: 4
  },
  rail: {
    height: 6,
    borderRadius: 4
  }
})(Slider);

const VaxYesRegisterPayment = () => {
  const { state = {} } = useLocation();
  const history = useHistory();
  const stripe = useStripe();
  const { enqueueSnackbar } = useSnackbar();
  const { phone_number } = useConsumerAuth();
  const { t } = useTranslation();

  const [drawerClearance, setDrawerClearance] = useState(0);
  const [value, setValue] = useState(2);
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const [awaitingRedirect, setAwaitingRedirect] = useState(false);
  const [country, setCountry] = useState("US");

  const [postVaxYesPay, { loading }] = useMutation(POST_VAX_YES_PAY, {
    onError: () => {
      enqueueSnackbar(t("vax_yes.vax_yes_register_payment.api_payment_error"), {
        variant: "error"
      });
    },
    onCompleted: ({ payment }) => {
      const { status, payment_checkout_session, session_id } = payment;

      if (status && status === "success" && session_id) {
        if (payment_checkout_session) {
          setAwaitingRedirect(true);

          const paymentSession = {
            session_id,
            payment_checkout_session,
            first_name: state.first_name
          };

          localStorage.setItem(
            "PaymentSession",
            JSON.stringify(paymentSession)
          );

          return stripe.redirectToCheckout({
            sessionId: payment_checkout_session
          });
        } else {
          history.push({
            pathname: `${ROUTE_PATHS.VAXYES.REGISTER_BASE}/success`,
            state: { first_name: state.first_name }
          });
        }
      } else {
        setShowErrorMsg(true);
      }
    }
  });

  useEffect(() => {
    if (!phone_number || !state.first_name || !state.last_name || !state.dob)
      return history.push("/");

    const { country } = parsePhoneNumber(phone_number);

    if (country === "MX") {
      setCountry("MX");
      setValue(5);
    }
  }, [state]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleNext = () => {
    postVaxYesPay({
      variables: {
        body: {
          phone_number: phone_number,
          first_name: state.first_name,
          last_name: state.last_name,
          dob: state.dob,
          amount: value,
          currency: "USD"
        }
      }
    });
  };

  return (
    <Sheet
      data-cy="vax-yes-register-view"
      style={{ paddingBottom: `${drawerClearance}px` }}
    >
      <SheetContent>
        <Box mb={2}>
          <Paper>
            <Box p={2} pt={3} style={{ overflowX: "hidden" }}>
              {loading || awaitingRedirect ? (
                <Box textAlign="center">
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <IntroText>
                    {t("vax_yes.vax_yes_register_payment.intro_text")}
                  </IntroText>
                  <Typography>
                    {country === "US"
                      ? t("vax_yes.vax_yes_register_payment.secondary_text_us")
                      : t(
                          "vax_yes.vax_yes_register_payment.secondary_text_non_us"
                        )}
                  </Typography>
                  {showErrorMsg && (
                    <Box mt={2}>
                      <Alert severity="error">
                        {t("vax_yes.vax_yes_register_payment.error_message")}
                      </Alert>
                    </Box>
                  )}
                  <Box mt={3} textAlign="center">
                    <Typography variant="h1">
                      <strong>${value}</strong>
                    </Typography>
                  </Box>
                  <Box mb={2} p={1}>
                    <CustomSlider
                      defaultValue={country === "US" ? 2 : 5}
                      min={country === "US" ? 0 : 5}
                      max={10}
                      step={1}
                      onChange={handleChange}
                    />
                  </Box>
                  {country === "US" && (
                    <Box my={2} textAlign="center">
                      <Typography>
                        {t(
                          "vax_yes.vax_yes_register_payment.contribution_text_us"
                        )}
                      </Typography>
                    </Box>
                  )}
                  <FormActionDrawer
                    setDrawerClearance={setDrawerClearance}
                    nextButtonLabel={t(
                      "vax_yes.vax_yes_register_payment.button"
                    )}
                    handleNext={handleNext}
                  />
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </SheetContent>
    </Sheet>
  );
};

export { VaxYesRegisterPayment };
